"use client";
import Image from "next/image";
import React, { useRef, useState } from "react";
import { zapierWebhook } from "@/lib/utils";

type GoalFormProps = {
  submitButton: string;
  darkBackground?: boolean;
};

const COUNTRY_CODE = "+592"; // GY – Guyana

const GoalForm: React.FC<GoalFormProps> = ({
  darkBackground = false,
  submitButton = "",
}) => {
  const initialState = {
    name: "",
    email: "",
    phone: "",
  };

  const [formFields, setFormFields] = useState(initialState);
  const [phoneError, setPhoneError] = useState<string>("");
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [connectWhatsApp, setConnectWhatsApp] = useState<boolean>(true);

  const formRef = useRef<HTMLFormElement>(null);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow digits only
    const digits = e.target.value.replace(/\D/g, "");
    setFormFields((prev) => ({ ...prev, phone: digits }));
    if (phoneError) setPhoneError("");
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConnectWhatsApp(e.target.checked);
  };

  const validatePhone = (): boolean => {
    if (!formFields.phone) {
      setPhoneError("Phone number is required.");
      return false;
    }
    // GY local numbers are 7 digits
    if (formFields.phone.length !== 7) {
      setPhoneError("Enter a valid 7-digit GY phone number.");
      return false;
    }
    setPhoneError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validatePhone()) return;

    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData);

    const payload = {
      name: data.name as string,
      email: data.email as string,
      phone: `${COUNTRY_CODE}${formFields.phone}`,
      timestamp: new Date().toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour12: false,
      }),
    };

    try {
      const { error } = await zapierWebhook(payload);

      if (!error) {
        (window as any).gtag?.("event", "conversion", {
          send_to: "AW-16816962332/VPOcCLKM6rMcEJz2-dI-",
        });
        setCurrentStep(2);
        formRef.current?.reset();
        setFormFields(initialState);
        setConnectWhatsApp(true);
      } else {
        setCurrentStep(1);
        console.error("Zapier Webhook Error:", error);
      }
    } catch (error) {
      console.error("Network Error:", error);
    }
  };

  const inputBase = darkBackground
    ? "text-[#ADADAD] bg-[#070C0F] border-[#070C0F] placeholder:text-[#ADADAD]"
    : "text-[#595858] bg-[#ffffff] border-[#DEDEDE] placeholder:text-[#595858]";

  return (
    <div>
      {currentStep === 1 && (
        <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col">
          {/* Name */}
          <div className="mb-2">
            <label htmlFor="name" className="sr-only">
              Full Name
            </label>
            <input
              id="name"
              type="text"
              name="name"
              placeholder="Full Name*"
              required
              className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none ${inputBase}`}
            />
          </div>

          {/* Email */}
          <div className="mb-2">
            <label htmlFor="email" className="sr-only">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              name="email"
              placeholder="E-mail*"
              required
              className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none ${inputBase}`}
            />
          </div>

          {/* Phone – fixed GY country code */}
          <div className="mb-2">
            <div
              className={`flex items-center w-full md:h-[42px] lg:h-[42px] h-[42px] rounded-xl border overflow-hidden ${
                phoneError
                  ? "border-red-500"
                  : darkBackground
                    ? "border-[#070C0F] bg-black"
                    : "border-[#DEDEDE] bg-white"
              }`}
            >
              {/* Fixed prefix */}
              <span
                className={`flex-shrink-0 px-3 text-sm font-medium border-r ${
                  darkBackground
                    ? "text-[#ADADAD] bg-[#070C0F] border-[#333]"
                    : "text-[#595858] bg-[#f5f5f5] border-[#DEDEDE]"
                }`}
              >
                {COUNTRY_CODE}
              </span>

              {/* Local number */}
              <label htmlFor="phone" className="sr-only">
                Phone Number
              </label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={formFields.phone}
                onChange={handlePhoneChange}
                placeholder="7-digit number*"
                maxLength={7}
                inputMode="numeric"
                className={`flex-1 h-full !text-sm md:text-base font-medium py-3 px-3 leading-tight focus-visible:outline-none appearance-none ${
                  darkBackground
                    ? "text-[#ADADAD] bg-[#070C0F] placeholder:text-[#ADADAD]"
                    : "text-[#595858] bg-[#ffffff] placeholder:text-[#595858]"
                }`}
              />
            </div>

            {phoneError && (
              <p className="mt-1 text-xs text-red-500">{phoneError}</p>
            )}
          </div>

          {/* WhatsApp consent */}
          <div className="mb-2 ml-1 flex items-center justify-start">
            <input
              id="page"
              name="connectWhatsApp"
              type="checkbox"
              checked={connectWhatsApp}
              onChange={handleCheckboxChange}
              className={`relative w-5 h-5 md:min-w-[20px] appearance-none cursor-pointer
                            rounded-full mr-2 border-2 ${darkBackground ? "border-white" : "border-white"}
                            checked:bg-[#39f] checked:border-0
                            after:content-['✓'] after:absolute after:top-1/2 after:left-1/2
                            after:-translate-x-1/2 after:-translate-y-1/2 after:text-[10px]
                            after:font-bold after:text-white after:opacity-0
                            checked:after:opacity-100 after:transition-opacity after:duration-200`}
            />

            <label
              htmlFor="page"
              className={`text-[12px] md:text-[12px] font-medium tracking-[-0.373px] cursor-pointer mb-2 ${
                darkBackground ? "text-white" : "text-white"
              }`}
            >
              I agree to receive updates via
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/whatsapp_icon.webp"
                alt="Whatsapp logo"
                width={12}
                height={12}
                className="mx-[2px] inline-block"
              />
              WhatsApp, Email & SMS.
              <a
                href="/privacy"
                target="_blank"
                className={`ml-1 underline md:block ${
                  darkBackground ? "text-[#FFBA08]" : "text-[#FFBA08]"
                }`}
              >
                Privacy Policy
              </a>
            </label>
          </div>

          <button
            type="submit"
            aria-label={submitButton}
            className="font-semibold bg-[#2A85FF] text-white text-xs md:text-sm py-2 capitalize rounded-[24px_24px_24px_0px]"
          >
            {submitButton}
          </button>
        </form>
      )}

      {currentStep === 2 && (
        <div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-16 w-16 mx-auto mb-6 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
            />
          </svg>
          <h3 className="text-[#fff] md:text-2xl text-xl font-bold text-center mb-2">
            Thank you for your ​i​nquiry.
          </h3>
          <h2 className="text-[#fff] md:text-xl text-lg font-normal text-center">
            Our programme advisor will get back to you shortly!
          </h2>
        </div>
      )}
    </div>
  );
};

export default GoalForm;
